import { create } from 'zustand';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../lib/api.js';

type UserRole = 'USER' | 'ADMIN';

export type AdminUser = {
  id: string;
  name: string | null;
  email: string;
  role: UserRole;
  bannedAt: string | null;
  isCompany: boolean;
  experimentalInterview?: boolean;
  createdAt: string;
  _count?: { simulations: number; packs: number };
  packs?: Array<{ type: string; refundedAt: string | null }>;
  simulations?: Array<{ createdAt: string; completedAt: string | null }>;
};

export type AdminSimulation = {
  id: string;
  /** English-canonical role; `rolePl` carries its Polish label for display. */
  role: string;
  rolePl: string | null;
  status: string;
  interviewType: string;
  primaryLanguage: 'PL' | 'EN' | 'DE';
  assessmentLanguage: 'PL' | 'EN' | 'DE' | null;
  createdAt: string;
  completedAt: string | null;
  stages?: Array<{ stageNumber: number; status: string }>;
  user: { id: string; email: string; name: string | null };
};

export type AdminPayment = {
  id: string;
  userId: string;
  type: string;
  totalSessions: number;
  usedSessions: number;
  refundedAt: string | null;
  stripePaymentId: string | null;
  stripeSubscriptionId: string | null;
  purchasedAt: string;
  user: { email: string; name: string | null };
  /** Real plan name for subscription packs; null for legacy one-time packs. */
  planDefinition: { nameEn: string; slug: string; priceCents: number; currency: string } | null;
};

type AnalyticsOverview = {
  totalUsers: number;
  activeUsers7d: number;
  activeUsers30d: number;
  totalSimulations: number;
  activeSimulations: number;
  simulationsToday: number;
  totalRevenue: number;
  revenue30d: number;
  revenueSummary: Array<{ currency: string; total: number; last30d: number }>;
  popularRoles: Array<{ role: string; rolePl: string | null; count: number }>;
  popularInterviewTypes: Array<{ type: string; count: number }>;
  averageSimulationDuration: number;
  packsSold: { STARTER: number; PRO: number; EXPERT: number };
  windowDays: number;
  usersGrowth: Array<{ date: string; value: number }>;
  simulationsPerDay: Array<{ date: string; value: number }>;
  planSales: Array<{ name: string; count: number; revenueCents: number; currency: string }>;
  stageDurations: Array<{ stageNumber: number; avgSeconds: number }>;
};

export type SystemPrompt = {
  id: string;
  key: string;
  name: string;
  description: string | null;
  content: string;
  variables: Record<string, unknown>;
  version: number;
  updatedAt: string;
};

export type TestRun = {
  id: string;
  modelUsed: string | null;
  totalCost: number | null;
  totalTokens: number | null;
  config: Record<string, unknown>;
  generatedQuestions: Record<string, unknown> | null;
  testAnswers: Record<string, string> | null;
  generatedReport: Record<string, unknown> | null;
  createdAt: string;
};

export type InterviewSettings = {
  primaryModel: string;
  fallbackModel: string;
  reportModel: string;
  reportFallbackModel: string;
  stage1QuestionCount: number;
  stage2QuestionCount: number;
  stage3QuestionCount: number;
  stage1AnswerMode: 'SPEECH' | 'TEXT' | 'BOTH';
  stage2AnswerMode: 'SPEECH' | 'TEXT' | 'BOTH';
  stage3AnswerMode: 'SPEECH' | 'TEXT' | 'BOTH';
  languageCheckQuestionCount: number;
  languageCheckAnswerMode: 'SPEECH' | 'TEXT' | 'BOTH';
  timeLowSeconds: number;
  timeMediumSeconds: number;
  timeHighSeconds: number;
  timeOvertimeMultiplier: number;
  codeAnswerMaxChars: number;
};

export type InterviewSettingsOptions = {
  models: string[];
  answerModes: Array<'SPEECH' | 'TEXT' | 'BOTH'>;
  questionCount: { min: number; max: number; languageCheckMax?: number };
};

export type SupportMessageStatus = 'NEW' | 'IN_PROGRESS' | 'RESOLVED' | 'SPAM';
export type SupportCategory = 'BUG_REPORT' | 'FEATURE_REQUEST' | 'BILLING_QUESTION' | 'ACCOUNT_HELP' | 'OTHER';

export type AdminSupportMessage = {
  id: string;
  userId: string | null;
  email: string;
  name: string | null;
  subject: string;
  category: SupportCategory;
  message: string;
  status: SupportMessageStatus;
  internalNote: string | null;
  userAgent: string | null;
  referrer: string | null;
  pageUrl: string | null;
  ipAddress: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  createdAt: string;
  updatedAt: string;
  user?: { id: string; email: string; name: string | null } | null;
  resolver?: { id: string; email: string; name: string | null } | null;
};

export type TestLabAnswerGeneratorConfig = {
  candidateLevel: 'LOW' | 'MID' | 'HIGH';
  professionalism: 'LOW' | 'MEDIUM' | 'HIGH';
  honesty: 'HONEST' | 'MIXED' | 'BLUFFER';
  languageLevel: 'BASIC' | 'INTERMEDIATE' | 'ADVANCED' | 'NATIVE';
  technicalLevel: 'JUNIOR' | 'MID' | 'SENIOR' | 'EXPERT';
  characterType: 'CALM' | 'CONFIDENT' | 'STRESSED' | 'TALKATIVE' | 'DIRECT';
};

type AdminState = {
  users: AdminUser[];
  usersTotal: number;
  usersPage: number;
  usersTotalPages: number;
  simulations: AdminSimulation[];
  simulationsTotal: number;
  simulationsPage: number;
  simulationsTotalPages: number;
  simulationsStatusCounts: { notStarted: number; inProgress: number; completed: number; deleted: number } | null;
  payments: AdminPayment[];
  analytics: AnalyticsOverview | null;
  prompts: SystemPrompt[];
  testRuns: TestRun[];
  interviewSettings: InterviewSettings | null;
  interviewSettingsOptions: InterviewSettingsOptions | null;
  supportMessages: AdminSupportMessage[];
  supportTotal: number;
  supportPage: number;
  supportLimit: number;
  supportNewCount: number;
  logs: Array<Record<string, unknown>>;
  isLoading: boolean;
  loadUsers: (params?: Record<string, string | number>) => Promise<void>;
  loadUserDetail: (id: string) => Promise<Record<string, unknown>>;
  updateUserRole: (id: string, role: UserRole) => Promise<void>;
  banUser: (id: string, reason: string) => Promise<void>;
  unbanUser: (id: string) => Promise<void>;
  adjustPack: (id: string, delta: number, reason: string) => Promise<void>;
  loadSimulations: (params?: Record<string, string | number>) => Promise<void>;
  getSimulation: (id: string) => Promise<Record<string, unknown>>;
  deleteSimulation: (id: string) => Promise<void>;
  loadPayments: (params?: Record<string, string | number>) => Promise<void>;
  refundPayment: (
    id: string,
    payload: { amount?: number; refundPack: boolean; reason: string },
  ) => Promise<void>;
  loadAnalytics: (days?: number) => Promise<void>;
  loadLogs: (kind: 'admin' | 'errors' | 'injection-attempts') => Promise<void>;
  loadPrompts: () => Promise<void>;
  createPrompt: (payload: {
    key: string;
    name: string;
    description?: string;
    content?: string;
    variables?: Record<string, unknown>;
  }) => Promise<SystemPrompt>;
  getPrompt: (key: string) => Promise<Record<string, unknown>>;
  updatePrompt: (
    key: string,
    payload: { content?: string; name?: string; description?: string | null },
  ) => Promise<void>;
  rollbackPrompt: (key: string, version: number) => Promise<void>;
  testPrompt: (key: string, variables: Record<string, unknown>) => Promise<string>;
  generateTestQuestions: (
    payload: Record<string, unknown>,
  ) => Promise<{ testRunId: string; questions: Record<string, unknown>; costInfo: Record<string, unknown> }>;
  generateTestReport: (
    payload: Record<string, unknown>,
  ) => Promise<{
    report: Record<string, unknown>;
    stageReports?: Record<string, Record<string, unknown>>;
    metadata: Record<string, unknown>;
  }>;
  generateTestAnswers: (payload: {
    model: string;
    questions: Record<string, unknown>;
    primaryLanguage: 'PL' | 'EN' | '';
    generatorConfig: TestLabAnswerGeneratorConfig;
  }) => Promise<{ answers: Record<string, string>; metadata: Record<string, unknown> }>;
  loadTestRuns: () => Promise<void>;
  getTestRun: (id: string) => Promise<TestRun>;
  deleteTestRun: (id: string) => Promise<void>;
  loadInterviewSettings: () => Promise<void>;
  updateInterviewSettings: (payload: InterviewSettings) => Promise<void>;
  loadSupportMessages: (params?: {
    status?: SupportMessageStatus;
    category?: SupportCategory;
    search?: string;
    page?: number;
    limit?: number;
  }) => Promise<void>;
  loadSupportMessageDetail: (id: string) => Promise<AdminSupportMessage>;
  updateSupportMessage: (
    id: string,
    payload: { status?: SupportMessageStatus; internalNote?: string },
  ) => Promise<AdminSupportMessage>;
  loadSupportNewCount: () => Promise<number>;
};

function withParams(path: string, params?: Record<string, string | number>) {
  if (!params) return path;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    search.set(key, String(value));
  }
  return `${path}?${search.toString()}`;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  users: [],
  usersTotal: 0,
  usersPage: 1,
  usersTotalPages: 1,
  simulations: [],
  simulationsTotal: 0,
  simulationsPage: 1,
  simulationsTotalPages: 1,
  simulationsStatusCounts: null,
  payments: [],
  analytics: null,
  prompts: [],
  testRuns: [],
  interviewSettings: null,
  interviewSettingsOptions: null,
  supportMessages: [],
  supportTotal: 0,
  supportPage: 1,
  supportLimit: 50,
  supportNewCount: 0,
  logs: [],
  isLoading: false,

  loadUsers: async (params) => {
    set({ isLoading: true });
    try {
      const data = await apiGet<{
        users: AdminUser[];
        total: number;
        page: number;
        totalPages: number;
      }>(withParams('/admin/users', params));
      set({
        users: data.users ?? [],
        usersTotal: data.total ?? 0,
        usersPage: data.page ?? 1,
        usersTotalPages: data.totalPages ?? 1,
      });
    } finally {
      set({ isLoading: false });
    }
  },

  loadUserDetail: async (id) => {
    return apiGet<Record<string, unknown>>(`/admin/users/${id}`);
  },

  updateUserRole: async (id, role) => {
    await apiPatch(`/admin/users/${id}/role`, { role });
    await get().loadUsers();
  },

  banUser: async (id, reason) => {
    await apiPost(`/admin/users/${id}/ban`, { reason });
    await get().loadUsers();
  },

  unbanUser: async (id) => {
    await apiPost(`/admin/users/${id}/unban`);
    await get().loadUsers();
  },

  adjustPack: async (id, delta, reason) => {
    // Suppress the global error toast — UserDetailPage shows a translated,
    // context-specific message for the known failure codes (UNLIMITED_PLAN,
    // NO_SESSIONS_TO_REMOVE) instead.
    await apiPost(`/admin/users/${id}/adjust-pack`, { delta, reason }, { suppressErrorToast: true });
  },

  loadSimulations: async (params) => {
    set({ isLoading: true });
    try {
      const data = await apiGet<{
        simulations: AdminSimulation[];
        total: number;
        page: number;
        totalPages: number;
        statusCounts?: { notStarted: number; inProgress: number; completed: number; deleted: number };
      }>(
        withParams('/admin/simulations', params),
      );
      set({
        simulations: data.simulations ?? [],
        simulationsTotal: data.total ?? 0,
        simulationsPage: data.page ?? 1,
        simulationsTotalPages: data.totalPages ?? 1,
        simulationsStatusCounts: data.statusCounts ?? null,
      });
    } finally {
      set({ isLoading: false });
    }
  },

  getSimulation: async (id) => {
    return apiGet<Record<string, unknown>>(`/admin/simulations/${id}`);
  },

  deleteSimulation: async (id) => {
    await apiDelete(`/admin/simulations/${id}`);
    await get().loadSimulations();
  },

  loadPayments: async (params) => {
    const data = await apiGet<{ payments: AdminPayment[] }>(withParams('/admin/payments', params));
    set({ payments: data.payments ?? [] });
  },

  refundPayment: async (id, payload) => {
    await apiPost(`/admin/payments/${id}/refund`, payload);
    await get().loadPayments();
  },

  loadAnalytics: async (days = 30) => {
    const analytics = await apiGet<AnalyticsOverview>(`/admin/analytics/overview?days=${days}`);
    set({ analytics });
  },

  loadLogs: async (kind) => {
    const data = await apiGet<{ logs: Array<Record<string, unknown>> }>(`/admin/logs/${kind}`);
    set({ logs: data.logs ?? [] });
  },

  loadPrompts: async () => {
    const data = await apiGet<{ prompts: SystemPrompt[] }>('/admin/prompts');
    set({ prompts: data.prompts ?? [] });
  },

  createPrompt: async (payload) => {
    const data = await apiPost<{ prompt: SystemPrompt }>('/admin/prompts', payload);
    await get().loadPrompts();
    return data.prompt;
  },

  getPrompt: async (key) => {
    return apiGet<Record<string, unknown>>(`/admin/prompts/${key}`);
  },

  updatePrompt: async (key, payload) => {
    await apiPatch(`/admin/prompts/${key}`, payload);
    await get().loadPrompts();
  },

  rollbackPrompt: async (key, version) => {
    await apiPost(`/admin/prompts/${key}/rollback`, { version });
  },

  testPrompt: async (key, variables) => {
    const data = await apiPost<{ rendered: string }>(`/admin/prompts/${key}/test`, { variables });
    return data.rendered;
  },

  generateTestQuestions: async (payload) => {
    return apiPost('/admin/test-lab/generate-questions', payload);
  },

  generateTestReport: async (payload) => {
    return apiPost('/admin/test-lab/generate-report', payload);
  },

  generateTestAnswers: async (payload) => {
    return apiPost('/admin/test-lab/generate-answers', payload);
  },

  loadTestRuns: async () => {
    const data = await apiGet<{ runs: TestRun[] }>('/admin/test-lab/runs');
    set({ testRuns: data.runs ?? [] });
  },

  getTestRun: async (id) => {
    const data = await apiGet<{ run: TestRun }>(`/admin/test-lab/runs/${id}`);
    return data.run;
  },

  deleteTestRun: async (id) => {
    await apiDelete(`/admin/test-lab/runs/${id}`);
    await get().loadTestRuns();
  },

  loadInterviewSettings: async () => {
    const data = await apiGet<{ settings: InterviewSettings; options: InterviewSettingsOptions }>(
      '/admin/interview-settings',
    );
    set({
      interviewSettings: data.settings ?? null,
      interviewSettingsOptions: data.options ?? null,
    });
  },

  updateInterviewSettings: async (payload) => {
    const data = await apiPut<{ settings: InterviewSettings }>('/admin/interview-settings', payload);
    set({ interviewSettings: data.settings ?? payload });
  },

  loadSupportMessages: async (params) => {
    const data = await apiGet<{
      messages: AdminSupportMessage[];
      total: number;
      page: number;
      limit: number;
    }>(withParams('/admin/support/messages', params as Record<string, string | number> | undefined));
    set({
      supportMessages: data.messages ?? [],
      supportTotal: data.total ?? 0,
      supportPage: data.page ?? 1,
      supportLimit: data.limit ?? 50,
    });
  },

  loadSupportMessageDetail: async (id) => {
    const data = await apiGet<{ message: AdminSupportMessage }>(`/admin/support/messages/${id}`);
    return data.message;
  },

  updateSupportMessage: async (id, payload) => {
    const data = await apiPatch<{ message: AdminSupportMessage }>(`/admin/support/messages/${id}`, payload);
    return data.message;
  },

  loadSupportNewCount: async () => {
    const data = await apiGet<{ total: number }>('/admin/support/messages?status=NEW&page=1&limit=1');
    const total = data.total ?? 0;
    set({ supportNewCount: total });
    return total;
  },
}));
